import React, { useState } from 'react';
import { useAppContext } from '../../AppContext';
import { httpsCallable } from 'firebase/functions';
import { motion } from 'framer-motion';

const DeleteAccountView = () => {
    const { appFunctions, setMessage, signOutUser, LucideIcons } = useAppContext();
    const [confirmationText, setConfirmationText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState('');

    const handleDeleteAccount = async () => {
        if (confirmationText !== 'DELETE') {
            setError('Please type DELETE to confirm.');
            return;
        }
        setIsDeleting(true);
        setError('');

        const deleteUserAccount = httpsCallable(appFunctions, 'deleteUserAccount');

        try {
            await deleteUserAccount();
            // The function will delete the user, which should trigger onAuthStateChanged to sign out.
            // However, we can force a sign-out on the client as well.
            await signOutUser();
            setMessage('Your account has been successfully deleted.');
        } catch (err) {
            console.error('Error deleting account:', err);
            setError(`An error occurred: ${err.message}. Please try again.`);
            setIsDeleting(false);
        }
    };

    return (
        <div className="p-4 md:p-6 bg-slate-800/50 rounded-2xl border border-red-500/50 shadow-lg max-w-2xl mx-auto">
            <div className="flex items-center mb-6">
                <div className="bg-red-500/20 p-3 rounded-full mr-4">
                    <LucideIcons.AlertTriangle size={28} className="text-red-400" />
                </div>
                <h2 className="text-3xl font-bold text-red-300 font-playfair">Delete Account</h2>
            </div>

            <div className="space-y-4 text-gray-300">
                <p className="font-semibold text-lg">This is a permanent and irreversible action.</p>
                <p>
                    When you delete your account, all of your data will be permanently removed from our servers. This includes:
                </p>
                <ul className="list-disc list-inside pl-4 space-y-2 bg-black/20 p-4 rounded-lg">
                    <li>Your profile information (displayName, bio, avatar, etc.).</li>
                    <li>All whispers, comments, and moments you've created.</li>
                    <li>Your connections, messages, and notifications.</li>
                    <li>Your Harmony Pro subscription and token balance.</li>
                    <li>All other data associated with your account.</li>
                </ul>
                <p className="font-bold text-red-400">
                    This action cannot be undone. If you are sure you want to proceed, please type "DELETE" in the box below.
                </p>
            </div>

            <div className="mt-8">
                <label htmlFor="confirmation" className="block text-sm font-medium text-gray-400 mb-2">
                    Type "DELETE" to confirm
                </label>
                <input
                    type="text"
                    id="confirmation"
                    value={confirmationText}
                    onChange={(e) => setConfirmationText(e.target.value)}
                    placeholder="DELETE"
                    className="w-full bg-slate-900/80 border-2 border-slate-700 rounded-lg p-3 text-white focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                />
            </div>

            {error && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 text-red-400 bg-red-900/50 p-3 rounded-lg text-center"
                >
                    {error}
                </motion.div>
            )}

            <div className="mt-6">
                <button
                    onClick={handleDeleteAccount}
                    disabled={confirmationText !== 'DELETE' || isDeleting}
                    className="w-full flex items-center justify-center p-4 bg-red-600 text-white font-bold rounded-lg transition-all duration-300 ease-in-out disabled:bg-gray-600 disabled:cursor-not-allowed hover:enabled:bg-red-700 focus:enabled:ring-4 focus:enabled:ring-red-500/50"
                >
                    {isDeleting ? (
                        <>
                            <LucideIcons.Loader size={20} className="animate-spin mr-2" />
                            Deleting Account...
                        </>
                    ) : (
                        <>
                            <LucideIcons.Trash2 size={20} className="mr-2" />
                            Permanently Delete My Account
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default DeleteAccountView;
